import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, Share, Bookmark, Watch, Edit, MessageSquare, BarChart3, FileText, Bot, Eye, Heart, Star, User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AddReviewModal from '../components/AddReviewModal';
import axios from 'axios';
import { reviewResultsToMarkdown, coerceToObject } from '../utils/jsonToMarkdown';
import { useUser } from '@clerk/clerk-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://47.251.43.229:35001';

const SubmissionDetail = () => {
  const { id } = useParams(); // This could be aixiv.251113.000001 or aixiv.251113.000001v1.5
  const navigate = useNavigate();
  const { user } = useUser(); // Get current logged-in user
  const [activeTab, setActiveTab] = useState('content');
  
  // Parse the URL to extract aixiv_id and version
  const parseIdAndVersion = (urlParam) => {
    // 匹配格式：aixiv.251113.000001v1.5 或者 aixiv.251113.000001
    const versionMatch = urlParam.match(/^(.+?)v(\d+(?:\.\d+)*)$/);
    if (versionMatch) {
      return {
        aixivId: versionMatch[1],
        requestedVersion: versionMatch[2]
      };
    }
    return {
      aixivId: urlParam,
      requestedVersion: null
    };
  };
  
  const { aixivId, requestedVersion } = parseIdAndVersion(id);
  
  // API state
  const [submission, setSubmission] = useState(null);
  const [allVersions, setAllVersions] = useState([]);
  const [currentVersionData, setCurrentVersionData] = useState(null); // 当前查看的版本数据
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Review modal state
  const [showAddReviewModal, setShowAddReviewModal] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);
    // Fetch submission data from API
  const fetchSubmissionData = async (aixivId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all submissions to find the one with matching aixiv_id and all its versions
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/submissions/aixiv_id/${aixivId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const allSubmissions = await response.json();
      
      // Find all versions of the submission with this aixiv_id
      const versions = allSubmissions.filter(sub => sub.aixiv_id === aixivId);
      
      if (versions.length === 0) {
        throw new Error('Submission not found');
      }
      
      // Sort versions by version number (descending) to get the latest first
      const sortedVersions = versions.sort((a, b) => {
        const versionA = parseFloat(a.version || '1.0');
        const versionB = parseFloat(b.version || '1.0');
        return versionB - versionA;
      });
        // 获取要显示的版本
      let targetSubmission;
      
      if (requestedVersion) {
        // 查找指定版本
        targetSubmission = sortedVersions.find(v => v.version === requestedVersion);
        if (!targetSubmission) {
          throw new Error(`Version ${requestedVersion} not found`);
        }      } else {
        // 使用最新版本
        targetSubmission = sortedVersions[0];
      }

      // Transform the data to match the expected format
      const transformedSubmission = {
        id: targetSubmission.aixiv_id,
        title: targetSubmission.title,
        authors: targetSubmission.authors?.map(author => ({
          name: author,
          affiliation: '', // Not available in current schema
          orcid: '' // Not available in current schema
        })) || [],
        agents: [], // Could be determined based on your data
        authorshipType: targetSubmission.authorship_type || 'human',
        type: targetSubmission.doc_type || 'paper',
        status: targetSubmission.status || 'published',
        publishedDate: new Date(targetSubmission.created_at).toLocaleDateString(),
        abstract: targetSubmission.abstract || 'No abstract available',
        doi: targetSubmission.doi || null,
        categories: Array.isArray(targetSubmission.category) ? targetSubmission.category : typeof targetSubmission.category === 'string' ? targetSubmission.category.split(' / ') : ['General'],
        keywords: targetSubmission.keywords || [],
        metrics: {
          views: targetSubmission.views || 0,
          downloads: targetSubmission.downloads || 0,
          citations: targetSubmission.citations || 0,
          comments: targetSubmission.comments || 0,
          bookmarks: 0, // Not available in current schema
        },
        versions: sortedVersions.map(version => ({
          version: version.version || '1.0',
          date: new Date(version.created_at).toLocaleDateString(),
          changes: version.version === targetSubmission.version ? 'Current version' : 'Previous version',
          s3_url: version.s3_url
        })),
        isBookmarked: false,
        isWatching: false,
        correspondingAuthor: targetSubmission.corresponding_author,
        license: targetSubmission.license,
        s3_url: targetSubmission.s3_url,
        currentVersion: targetSubmission.version || '1.0',
        uploaded_by: targetSubmission.uploaded_by,
        is_public: targetSubmission.is_public || false
      };
        setSubmission(transformedSubmission);
      setAllVersions(sortedVersions);
      setCurrentVersionData(targetSubmission);      // Check access permission: if private, only uploader can view
      if (!transformedSubmission.is_public) {
        const currentUserId = user?.id;
        const uploaderId = transformedSubmission.uploaded_by;
        
        // console.log('Permission check:', { 
        //   is_public: transformedSubmission.is_public, 
        //   currentUserId, 
        //   uploaderId 
        // });
        
        if (!currentUserId || currentUserId !== uploaderId) {
          // For private papers, show "Submission not found" instead of permission error
          setError('Submission not found');
          setSubmission(null);
          return;
        }
      }
      
    } catch (err) {
      console.error('Error fetching submission:', err);
      setError(err.message || 'Failed to load submission. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  // Fetch reviews from API
  const fetchReviews = useCallback(async () => {
    if (!submission) return;
    
    try {
      setReviewsLoading(true);
      setReviewsError(null);
      const reviewPayload = {
        aixiv_id: submission.id,
        version: submission.currentVersion, // 使用当前查看的版本
        //start_date: '2024-01-01T00:00:00',
        end_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 19)
      };
      
      console.log('Fetching reviews with payload:', reviewPayload);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/get-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reviewPayload)
      });
      
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      if (!response.ok) {
        console.error('Response not ok:', response.status, responseText);
        
        // Handle 404 specifically (no reviews found)
        if (response.status === 404) {
          setReviews([]);
          return;
        }
        
        throw new Error(`Failed to fetch reviews: ${response.status} - ${responseText}`);
      }
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError);
        throw new Error('Invalid response format from server');
      }
      
      console.log('Parsed result:', result);
      
      if (result.code === 200 && result.review_list) {        
        // Sort reviews by create_time (most recent first)
        const sortedReviews = result.review_list.sort((a, b) => 
          new Date(b.create_time) - new Date(a.create_time)
        );
        // Transform API data to expected format
        const transformedReviews = sortedReviews.map((review, index) => ({
          id: `REV-${index + 1}`,
          reviewer: 'Anonymous Reviewer',
          date: new Date(review.create_time).toLocaleString(),
          summaryMd: reviewResultsToMarkdown(
            coerceToObject(review.review_results),
            { addHeader: false, addNote: false }
          ),
          strengths: [], // Not provided by API
          weaknesses: [], // Not provided by API
          recommendation: review.reviewer === "Official Agent" ? "Official Agent" : "Human Review", // review.reviewer
          helpful: 0, // Default since not provided
          createTime: review.create_time, // Keep original timestamp for future sorting if needed
        }));
        setReviews(transformedReviews);
      } else {
        console.log('No reviews found or unexpected response format');
        setReviews([]);
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setReviewsError(`Failed to load reviews: ${err.message}`);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  }, [submission]);

  // Handle review added
  const handleReviewAdded = () => {
    fetchReviews(); // Refresh reviews after adding a new one
  };  // Load data when component mounts or id changes
  useEffect(() => {
    if (id) {
      fetchSubmissionData(aixivId);
    }
  }, [id, aixivId, user]); // Add user as dependency to re-check when user logs in

  // Fetch reviews when submission is loaded
  useEffect(() => {
    if (submission) {
      fetchReviews();
    }
  }, [submission, fetchReviews]);

  // Call the view count interface
 const updateViewsAPI = async ({ aixiv_id, version, doc_type }) => {
  try {
    await axios.post(
      `${API_BASE_URL}/api/update-views`,
      null, // Body 为空
      {
        params: { aixiv_id, version, doc_type } // 使用 params 发送 query 参数
      }
    );
    console.log('Update views success');
  } catch (error) {
    console.error('Failed to update views:', error.response?.data || error);
  }
};

  const updateDownloadsAPI = async ({ aixiv_id, version, doc_type }) => {
    const params = { aixiv_id, version, doc_type };
    console.log('Calling /api/update-downloads with params:', params);
    if (!version) {
      console.error('Error: Version is undefined. Download count will not be updated.');
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/api/update-downloads`,
        null,
        { params }
      );
      console.log('Update downloads success');
    } catch (error) {
      console.error('Failed to update downloads:', error.response?.data || error);
    }
  };

  
  useEffect(() => {
    if (submission) {
      updateViewsAPI({
        aixiv_id: submission.id,
        version: submission.currentVersion,
        doc_type: submission.type,
      });
    }
  }, [submission]); 

  
const handleDownloadClick = (versionData) => {
  // Safety check: Ensure all necessary data is present before proceeding.
  if (!submission || !versionData || !versionData.version) {
    console.error("Download failed due to missing data:", {
      "submissionExists": !!submission,
      "receivedVersionData": versionData,
      "expectedVersion": versionData ? versionData.version : 'versionData is missing'
    });
    return; // Stop execution if data is incomplete
  }

  // Step 1: Call the API to update the download count.
  updateDownloadsAPI({
    aixiv_id: submission.id,
    version: versionData.version,
    doc_type: submission.type,
  });

  // Step 2: Build PDF proxy URL and open in new tab
  // 构建统一的 PDF URL: https://aixiv.science/pdf/aixiv.251117.000002v1.0
  const pdfProxyUrl = versionData.version === submission.versions[0].version
    ? `https://aixiv.science/pdf/${submission.id}` // 最新版本不带版本号
    : `https://aixiv.science/pdf/${submission.id}v${versionData.version}`; // 旧版本带版本号
  
  console.log('[PDF] Opening PDF proxy URL:', pdfProxyUrl);
  window.open(pdfProxyUrl, '_blank', 'noopener,noreferrer');
};

const handleViewClick = (versionData) => {
  // Safety check: Ensure all necessary data is present.
  if (!submission || !versionData || !versionData.version) {
      console.error("Cannot open PDF due to missing data:", {
        "submissionExists": !!submission,
        "receivedVersionData": versionData,
        "expectedVersion": versionData ? versionData.version : 'versionData is missing'
    });
    return; // Stop execution
  }
  
  // Call the API to update the view count every time the "View" button is clicked.
  updateViewsAPI({
      aixiv_id: submission.id,
      version: versionData.version,
      doc_type: submission.type
  });
  
  // Call the API to update the download count when viewing PDF.
  updateDownloadsAPI({
      aixiv_id: submission.id,
      version: versionData.version,
      doc_type: submission.type
  });
  
  // Build PDF proxy URL and open in new tab
  // 构建统一的 PDF URL: https://aixiv.science/pdf/aixiv.251117.000002v1.0
  const pdfProxyUrl = versionData.version === submission.versions[0].version
    ? `https://aixiv.science/pdf/${submission.id}` // 最新版本不带版本号
    : `https://aixiv.science/pdf/${submission.id}v${versionData.version}`; // 旧版本带版本号
  
  console.log('[PDF] Opening PDF proxy URL:', pdfProxyUrl);
  window.open(pdfProxyUrl, '_blank', 'noopener,noreferrer');
};

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading submission...</p>
        </div>
      </div>
    );
  }  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="mb-4">
            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            No Result
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error === 'Submission not found' 
              ? 'The submission you are looking for could not be found.' 
              : error}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button 
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              Back to Home
            </button>
            <button 
              onClick={() => navigate('/explore')}
              className="btn-secondary"
            >
              Explore Papers
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No submission found
  if (!submission) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">Submission not found</p>
        </div>
      </div>
    );
  }

  // const discussions = [
  //   {
  //     id: 'DISC-001',
  //     author: 'Dr. Sarah Chen',
  //     date: '2024-01-16',
  //     content: 'Excellent work! I\'m particularly interested in the scalability of the attention mechanism. Have you considered applying this to longer sequences?',
  //     replies: [
  //       {
  //         author: 'Ashish Vaswani',
  //         date: '2024-01-16',
  //         content: 'Thank you for the question! We did experiment with longer sequences and found that the quadratic complexity becomes a bottleneck. We\'re exploring sparse attention patterns in follow-up work.'
  //       }
  //     ],
  //     likes: 8
  //   },
  //   {
  //     id: 'DISC-002',
  //     author: 'Prof. Michael Zhang',
  //     date: '2024-01-17',
  //     content: 'The multi-head attention mechanism is brilliant. Could you elaborate on how you chose the number of heads and key dimensions?',
  //     replies: [],
  //     likes: 5
  //   }
  // ];

  // const similarPapers = [
  //   {
  //     id: 'SUB-2024-002',
  //     title: 'BERT: Pre-training of Deep Bidirectional Transformers',
  //     authors: ['Jacob Devlin', 'Ming-Wei Chang'],
  //     similarity: 0.85
  //   },
  //   {
  //     id: 'SUB-2024-003',
  //     title: 'GPT-3: Language Models are Few-Shot Learners',
  //     authors: ['Tom B. Brown', 'Benjamin Mann'],
  //     similarity: 0.78
  //   },
  //   {
  //     id: 'SUB-2024-004',
  //     title: 'Vision Transformer: An Image is Worth 16x16 Words',
  //     authors: ['Alexey Dosovitskiy', 'Lucas Beyer'],
  //     similarity: 0.72
  //   }
  // ];

  const renderContent = () => (
    <div className="prose dark:prose-invert max-w-none">
      <h2>Abstract</h2>
      <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
        {submission.abstract || 'No abstract available for this submission.'}
      </p>
      
      <div className="bg-gray-100 dark:bg-gray-700 p-6 rounded-lg my-8">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
          <p>Full PDF content would be displayed here</p>
          <p className="text-sm">Interactive PDF viewer with highlighting and comments</p>
          {submission.s3_url && (
            <button
              onClick={() => handleViewClick({ version: submission.currentVersion, s3_url: submission.s3_url })}
              className="inline-block mt-4 btn-primary"
            >
              View PDF
            </button>
          )}
        </div>
      </div>
    </div>
  );
  const renderVersions = () => (
    <div className="space-y-4">
      {allVersions.map((version, index) => {
        const isCurrentlyViewing = version.version === submission.currentVersion;
        const isLatest = index === 0;
        
        return (
          <div key={version.version || index} className={`card p-4 ${isCurrentlyViewing ? 'ring-2 ring-primary-500' : ''}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3">
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    Version {version.version || '1.0'}
                  </span>
                  {isLatest && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded text-sm">
                      Latest
                    </span>
                  )}
                  {isCurrentlyViewing && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-sm">
                      Viewing
                    </span>
                  )}
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                  {new Date(version.created_at).toLocaleDateString()} • 
                  {isLatest ? ' Latest version' : ' Previous version'}
                </p>
              </div>
              <div className="flex items-center space-x-2">                {!isCurrentlyViewing && (
                  <button
                    onClick={() => {
                      // 使用新的URL格式：aixiv.251113.000001v1.5
                      navigate(`/abs/${aixivId}v${version.version}`);
                    }}
                    className="btn-secondary text-sm flex items-center space-x-1"
                  >
                    <Eye className="h-4 w-4" />
                    <span>View</span>
                  </button>
                )}
                {version.s3_url ? (
                  <button
                    onClick={() => handleDownloadClick({ version: version.version, s3_url: version.s3_url })}
                    className="btn-primary text-sm flex items-center space-x-1"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download</span>
                  </button>
                ) : (
                  <span className="text-gray-400 text-sm">No file available</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
        {/* 添加回到最新版本的按钮 */}
      {requestedVersion && (
        <div className="mt-4 text-center">
          <button
            onClick={() => {
              navigate(`/${aixivId}`);
            }}
            className="btn-primary text-sm"
          >
            View Latest Version
          </button>
        </div>
      )}
    </div>
  );

  const renderReviews = () => (
    <div className="space-y-6">
      {/* Add Review Button */}
      <div className="flex justify-end">
        <button 
          onClick={() => setShowAddReviewModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Edit className="h-4 w-4" />
          <span>Add Review</span>
        </button>
              </div>

      {/* Reviews Loading State */}
      {reviewsLoading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Loading reviews...</p>
              </div>
      )}

      {/* Reviews Error State */}
      {reviewsError && (
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400 mb-4">{reviewsError}</p>
          <button 
            onClick={fetchReviews}
            className="btn-primary"
          >
            Try Again
          </button>
            </div>
      )}

      {/* No Reviews State */}
      {!reviewsLoading && !reviewsError && reviews.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-600 dark:text-gray-400">No reviews yet. Be the first to add a review!</p>
        </div>
      )}

      {/* Individual Reviews */}
      {!reviewsLoading && !reviewsError && reviews.map((review) => (
        <div key={review.id} className="card p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white">
                {review.reviewer}
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">{review.date}</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                {review.recommendation}
              </span>
              {review.helpful > 0 && (
              <div className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-400">
                <Heart className="h-4 w-4" />
                <span>{review.helpful}</span>
              </div>
              )}
            </div>
          </div>          <div className="space-y-4">
            <div>
              <h5 className="font-medium text-gray-900 dark:text-white mb-2">Review</h5>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({node, ...props}) => <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4" {...props} />,
                    h2: ({node, ...props}) => <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3 mt-6" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-4" {...props} />,
                    h4: ({node, ...props}) => <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2 mt-3" {...props} />,
                    p: ({node, ...props}) => <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 mb-3" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal list-inside text-gray-700 dark:text-gray-300 space-y-1 mb-3" {...props} />,
                    li: ({node, ...props}) => <li className="text-gray-700 dark:text-gray-300" {...props} />,
                    blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-600 dark:text-gray-400 my-4" {...props} />,
                    code: ({node, inline, ...props}) => 
                      inline 
                        ? <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm font-mono text-gray-800 dark:text-gray-200" {...props} />
                        : <code className="block bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm font-mono text-gray-800 dark:text-gray-200 overflow-x-auto" {...props} />,
                    strong: ({node, ...props}) => <strong className="font-bold text-gray-900 dark:text-white" {...props} />,
                    em: ({node, ...props}) => <em className="italic text-gray-700 dark:text-gray-300" {...props} />,
                  }}
                >
                  {review.summaryMd || '*No structured review content.*'}
                </ReactMarkdown>
              </div>
            </div>

            {review.strengths.length > 0 && (
            <div>
              <h5 className="font-medium text-gray-900 dark:text-white mb-2">Strengths</h5>
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
                {review.strengths.map((strength, index) => (
                  <li key={index}>{strength}</li>
                ))}
              </ul>
            </div>
            )}

            {review.weaknesses.length > 0 && (
            <div>
              <h5 className="font-medium text-gray-900 dark:text-white mb-2">Weaknesses</h5>
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
                {review.weaknesses.map((weakness, index) => (
                  <li key={index}>{weakness}</li>
                ))}
              </ul>
            </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  // const renderDiscussions = () => (
  //   <div className="space-y-6">
  //     {discussions.map((discussion) => (
  //       <div key={discussion.id} className="card p-6">
  //         <div className="flex items-start space-x-4">
  //           <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center text-white font-medium">
  //             {discussion.author.split(' ').map(n => n[0]).join('')}
  //           </div>
  //           <div className="flex-1">
  //             <div className="flex items-center space-x-2 mb-2">
  //               <span className="font-medium text-gray-900 dark:text-white">
  //                 {discussion.author}
  //               </span>
  //               <span className="text-sm text-gray-500 dark:text-gray-400">
  //                 {discussion.date}
  //               </span>
  //             </div>
  //             <p className="text-gray-700 dark:text-gray-300 mb-3">
  //               {discussion.content}
  //             </p>
  //             <div className="flex items-center space-x-4">
  //               <button className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-400 hover:text-red-500">
  //                 <Heart className="h-4 w-4" />
  //                 <span>{discussion.likes}</span>
  //               </button>
  //               <button className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700">
  //                 Reply
  //               </button>
  //             </div>

  //             {/* Replies */}
  //             {discussion.replies.length > 0 && (
  //               <div className="mt-4 ml-6 space-y-3">
  //                 {discussion.replies.map((reply, index) => (
  //                   <div key={index} className="border-l-2 border-gray-200 dark:border-gray-700 pl-4">
  //                     <div className="flex items-center space-x-2 mb-1">
  //                       <span className="font-medium text-gray-900 dark:text-white text-sm">
  //                         {reply.author}
  //                       </span>
  //                       <span className="text-xs text-gray-500 dark:text-gray-400">
  //                         {reply.date}
  //                       </span>
  //                     </div>
  //                     <p className="text-sm text-gray-700 dark:text-gray-300">
  //                       {reply.content}
  //                     </p>
  //                   </div>
  //                 ))}
  //               </div>
  //             )}
  //           </div>
  //         </div>
  //       </div>
  //     ))}

  //     {/* Add Comment */}
  //     <div className="card p-6">
  //       <h4 className="font-medium text-gray-900 dark:text-white mb-4">
  //         Add Comment
  //       </h4>
  //       <textarea
  //         className="input-field h-24 mb-4"
  //         placeholder="Share your thoughts or ask questions..."
  //       />
  //       <div className="flex justify-end">
  //         <button className="btn-primary">
  //           Post Comment
  //         </button>
  //       </div>
  //     </div>
  //   </div>
  // );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumbs */}
        <nav className="mb-6">
          <ol className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
            <li><a href="/" className="hover:text-gray-700 dark:hover:text-gray-300">Home</a></li>
            <li>/</li>
            <li><a href="/explore" className="hover:text-gray-700 dark:hover:text-gray-300">Explore</a></li>
            <li>/</li>
            <li className="text-gray-900 dark:text-white">{submission.id}</li>
          </ol>
        </nav>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">            {/* Header */}
            <div className="card p-6 mb-8">              {/* 版本提示条 */}
              {requestedVersion && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Eye className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-800 dark:text-blue-200">
                        You are viewing version <strong>{submission.currentVersion}</strong>
                      </span>
                    </div>
                    <button
                      onClick={() => navigate(`/${aixivId}`)}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      View Latest Version
                    </button>
                  </div>
                </div>
              )}              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  {/* Authorship Type Icon - Same style as Home.js */}
                  {submission.authorshipType === 'ai' ? (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                      <Bot className="h-3 w-3 mr-1" />
                      AI
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                      <User className="h-3 w-3 mr-1" />
                      Human
                    </span>
                  )}
                  
                  <span className={`px-3 py-1 rounded text-sm font-medium ${
                    submission.type === 'paper' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                  }`}>
                    {submission.type.toUpperCase()}
                  </span>
                  
                  {submission.agents.length > 0 && (
                    <span className="inline-flex items-center px-3 py-1 rounded text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                      <Bot className="h-4 w-4 mr-1" />
                      AI Co-authored
                    </span>
                  )}
                  
                  <span className={`px-3 py-1 rounded text-sm font-medium ${
                    submission.status === 'published' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  }`}>
                    {submission.status}
                  </span>
                  
                  {/* 版本标识 */}
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded text-sm font-medium">
                    v{submission.currentVersion}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  {submission.s3_url ? (
                    <button
                      onClick={() => handleDownloadClick({ version: submission.currentVersion, s3_url: submission.s3_url })}
                      className="btn-secondary flex items-center space-x-2"
                    >
                      <Download className="h-4 w-4" />
                      <span>PDF</span>
                    </button>
                  ) : (
                    <button
                      disabled
                      className="btn-secondary flex items-center space-x-2 opacity-50 cursor-not-allowed"
                    >
                      <Download className="h-4 w-4" />
                      <span>PDF</span>
                    </button>
                  )}
                  <button className="btn-secondary">
                    <Share className="h-4 w-4" />
                  </button>
                  {/* <button className={`btn-secondary ${submission.isBookmarked ? 'text-yellow-600' : ''}`}>
                    <Bookmark className="h-4 w-4" />
                  </button> */}
                  <button className={`btn-secondary ${submission.isWatching ? 'text-blue-600' : ''}`}>
                    <Watch className="h-4 w-4" />
                  </button>
                  
                  {submission.status === 'revision-needed' && (
                    <button className="btn-primary flex items-center space-x-2">
                      <Edit className="h-4 w-4" />
                      <span>New Version</span>
                    </button>
                  )}                </div>
              </div>              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {submission.title}
              </h1>              {/* Authors */}
              <div className="mb-4">
                {/* First Line: Authors - 14px (text-sm) */}
                <div className="text-sm mb-1">
                  <span className="font-semibold text-gray-600 dark:text-gray-400">Authors: </span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {submission.authors.map((author, index) => (
                      <span key={index}>
                        {author.name}
                        {index < submission.authors.length - 1 && ', '}
                      </span>
                    ))}
                  </span>
                </div>
                  {/* Second Line: Corresponding Author - 13px (slightly smaller) */}
                {submission.correspondingAuthor && (
                  <div className="text-[13px] text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">
                      {submission.authorshipType === 'ai' ? 'Corresponding Human: ' : 'Corresponding Author: '}
                    </span>
                    <span>
                      {submission.correspondingAuthor}
                    </span>
                  </div>
                )}
                
                {/* AI Agents Line if exists - 13px */}
                {submission.agents.length > 0 && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[13px] font-semibold text-gray-600 dark:text-gray-400">AI Agents:</span>
                    {submission.agents.map((agent, index) => (
                      <span key={index} className="inline-flex items-center gap-1 text-[13px] text-purple-600 dark:text-purple-400">
                        <Bot className="h-3 w-3" />
                        {agent.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Metrics */}
              <div className="flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
                <span className="flex items-center">
                  <Eye className="h-4 w-4 mr-1" />
                  {submission.metrics.views.toLocaleString()} views
                </span>
                <span className="flex items-center">
                  <Download className="h-4 w-4 mr-1" />
                  {submission.metrics.downloads.toLocaleString()} downloads
                </span>
                {/* <span className="flex items-center">
                  <MessageSquare className="h-4 w-4 mr-1" />
                  {submission.metrics.comments} comments
                </span>
                <span className="flex items-center">
                  <Star className="h-4 w-4 mr-1" />
                  {submission.metrics.citations} citations
                </span> */}
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
              <nav className="-mb-px flex space-x-8">
                {[
                  { id: 'content', label: 'Abstract / PDF', icon: FileText },
                  { id: 'versions', label: 'Versions', icon: Edit },
                  { id: 'reviews', label: 'Reviews', icon: MessageSquare },
                  // { id: 'discussion', label: 'Discussion', icon: MessageSquare },
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors flex items-center space-x-2 ${
                      activeTab === id
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div>
              {activeTab === 'content' && renderContent()}
              {activeTab === 'versions' && renderVersions()}
              {activeTab === 'reviews' && renderReviews()}
              {/* {activeTab === 'discussion' && renderDiscussions()} */}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Paper Info */}
            <div className="card p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Paper Info</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Published:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{submission.publishedDate}</span>
                </div>                <div>
                  <span className="text-gray-500 dark:text-gray-400">aiXiv ID:</span>
                  <button className="ml-2 text-primary-600 dark:text-primary-400 hover:underline">
                    {submission.id}
                  </button>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Categories:</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {submission.categories.map((category) => (
                      <span key={category} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Keywords:</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {submission.keywords.map((keyword) => (
                      <span key={keyword} className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
                {submission.license && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">License:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">{submission.license}</span>
                  </div>
                )}
                {submission.currentVersion && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Version:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">v{submission.currentVersion}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Live Metrics */}
            <div className="card p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <BarChart3 className="h-4 w-4 mr-2" />
                Live Metrics
              </h3>
              <div className="space-y-3">
                {Object.entries(submission.metrics).map(([key, value]) => {
              // Add conditional judgment, exclude comments,references and bookmarks
                if (key === 'comments' || key === 'citations'||key === 'bookmarks') {
                return null;
                }
                return (
                <div key={key} className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400 capitalize">{key}:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                {value.toLocaleString()}
                </span>
                </div>
             );
             })}
              </div>
            </div>

            {/* Similar Papers
            <div className="card p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Similar Papers</h3>
              <div className="space-y-3">
                {similarPapers.map((paper) => (
                  <div key={paper.id} className="group cursor-pointer">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors line-clamp-2">
                      {paper.title}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {paper.authors.join(', ')}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-400">
                        {Math.round(paper.similarity * 100)}% similar
                      </span>
                      <button className="text-xs text-primary-600 dark:text-primary-400 hover:underline">
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* Add Review Modal */}
      <AddReviewModal
        isOpen={showAddReviewModal}
        onClose={() => setShowAddReviewModal(false)}
        submission={submission}
        onReviewAdded={handleReviewAdded}
      />
    </div>
  );
};

export default SubmissionDetail;